import { type ReactElement } from 'react';
import { Link, redirect, useActionData, useNavigation, useParams } from 'react-router';
import type { ActionFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@marketplace/ui';
import { AuthField, FormAlert, useAuthForm } from '../features/auth/form.js';
import { InboxPanel, ResendButton } from '../features/auth/InboxPanel.js';
import { LoginSchema, type LoginValues } from '../features/auth/schema.js';
import {
  apiFrom,
  failureMessage,
  isResend,
  isUnverified,
  localeOf,
  resendVerification,
  throttled,
  type ResendOutcome,
} from '../features/auth/actions.js';
import { returnPathOf } from '../features/jobs/handshake.js';
import { seedSession } from '../shared/session.js';
import { type TranslationKey } from '../i18n/index.js';

/**
 * Sign in — one message for every refusal, and exactly one exception.
 *
 * `W2-T01` §4.5 makes a wrong password, an unknown address, a suspended account and a soft-deleted
 * one **byte-identical**: the same `401`, the same code, the same sentence. The page cannot tell
 * them apart, and that is the design rather than a limitation of it.
 *
 * The exception is `403 EMAIL_NOT_VERIFIED`, which better-auth throws *after* checking the
 * password — so it tells the person who typed the right password something they already know.
 * "Check your inbox" and "you are locked out" are different situations with different actions, and
 * a user who cannot tell them apart tries the password again forever.
 */
type LoginResult =
  | { kind: 'failed'; message: TranslationKey }
  | { kind: 'unverified'; email: string }
  | { kind: 'rate-limited'; seconds: number | undefined };

export async function action({
  request,
  params,
  context,
}: ActionFunctionArgs): Promise<LoginResult | ResendOutcome | Response> {
  const form = await request.formData();
  const locale = localeOf(params);

  if (isResend(form)) return resendVerification(context, locale, form);

  const parsed = LoginSchema.safeParse(Object.fromEntries(form));
  if (!parsed.success) return { kind: 'failed', message: 'auth.login.refused' };

  try {
    // **One API call, and this is the line that makes it one.** The sign-in response carries the
    // authenticated user, so seeding the session cache with it means the loader that runs after the
    // redirect finds warm data instead of asking `get-session` for something we were just told.
    // Invalidating here would be correct and would cost a second round trip on every login.
    seedSession(context, await apiFrom(context).signIn(parsed.data));
    // `W4-T09`: a page that sent the visitor here may ask to be returned to (`?next=`) — the QR's
    // landing page, whose token waits in `sessionStorage`. Only a plain path on this site, in this
    // language, is followed (`returnPathOf`); anything else goes home, as before.
    const next = returnPathOf(new URL(request.url).searchParams.get('next'), locale);
    return redirect(next ?? `/${locale}`);
  } catch (error) {
    if (isUnverified(error)) return { kind: 'unverified', email: parsed.data.email };
    // `W2-T07`: a throttle is a wait, not a refusal — it must never read as "wrong details".
    const limited = throttled(error);
    if (limited !== undefined) return { kind: 'rate-limited', seconds: limited.seconds };
    // `failureMessage` maps a `401` — every refusal — onto this page's single sentence, and leaves
    // a network failure and a server error saying what they are.
    return { kind: 'failed', message: failureMessage(error, 'auth.login.refused') };
  }
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const params = useParams();
  const locale = params['lang'] ?? '';
  const result = useActionData<LoginResult | ResendOutcome>();
  const navigation = useNavigation();
  const { form, onSubmit } = useAuthForm<LoginValues>({
    schema: LoginSchema,
    defaultValues: { email: '', password: '' },
  });

  const unverified = result !== undefined && 'email' in result ? result : undefined;

  return (
    <article data-testid="login">
      <h1>{t('auth.login.title')}</h1>

      {unverified === undefined ? (
        <>
          <form className="mp-form" onSubmit={onSubmit} noValidate>
            <AuthField
              control={form.control}
              name="email"
              type="email"
              autoComplete="email"
              label={t('auth.field.email.label')}
            />
            <AuthField
              control={form.control}
              name="password"
              type="password"
              autoComplete="current-password"
              label={t('auth.field.password.label')}
            />

            {result?.kind === 'failed' ? <FormAlert>{t(result.message)}</FormAlert> : null}
            {result?.kind === 'rate-limited' ? (
              <FormAlert>
                {result.seconds === undefined
                  ? t('auth.error.rateLimitedNoWait')
                  : t('auth.error.rateLimited', { count: result.seconds })}
              </FormAlert>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              isPending={navigation.state === 'submitting'}
              pendingLabel={t('auth.pending')}
            >
              {t('auth.login.submit')}
            </Button>
          </form>

          <p>
            <Link to={`/${locale}/reset-password`}>{t('auth.login.forgot')}</Link>
          </p>
          <p>
            <Link to={`/${locale}/signup`}>{t('auth.login.noAccount')}</Link>
          </p>
        </>
      ) : (
        <InboxPanel
          testId="inbox-panel"
          title={t('auth.inbox.title')}
          body={t('auth.inbox.body', { email: unverified.email })}
          status={unverified.kind === 'unverified' ? undefined : unverified.kind}
        >
          <ResendButton email={unverified.email} />
        </InboxPanel>
      )}
    </article>
  );
}
